/**
 * @fileoverview Single-select popover for indirect cost subcategories.
 * @module features/indirect-costs/components/IndirectCostSubcategorySelect
 */

import { useMemo, useState, useRef, type ReactElement } from 'react'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Spinner } from '@/components/ui/spinner'
import type { IndirectCostSubcategoryDTO } from '../schemas/indirectCostsSchema'

interface IndirectCostSubcategorySelectProps {
  /** Currently selected subcategory UUID */
  value: string | null
  /** Callback fired when user picks a different subcategory */
  onChange: (uuid: string) => void
  /** Collection of subcategories available to the user */
  subcategories: IndirectCostSubcategoryDTO[]
  /** Whether the options are still loading */
  isLoading?: boolean
  /** Whether fetching subcategories failed */
  isError?: boolean
  /** Error message to display when fetching failed */
  errorMessage?: string
  /** Placeholder rendered when no subcategory is selected */
  placeholder: string
  /** Label displayed when no options match the search */
  emptyLabel: string
  /** Label displayed while options load */
  loadingLabel: string
  /** Helper text shown below the selector */
  helperText?: string
  /** Disables interaction */
  disabled?: boolean
  /** Callback fired when popover open state changes */
  onOpenChange?: (isOpen: boolean) => void
  /** Callback to create a new subcategory inline - returns the new UUID */
  onCreateSubcategory?: (name: string) => Promise<string>
  /** Label for the create action */
  createLabel?: string
  /** Placeholder for the create input */
  createPlaceholder?: string
  /** Label for the create button */
  createButtonLabel?: string
  /** Whether the popover should be open on mount */
  defaultOpen?: boolean
  /** Custom trigger element (replaces default combobox button) */
  trigger?: ReactElement
  /** Additional class for the root wrapper */
  className?: string
}

/**
 * Popover-based single selector that supports searching within subcategories.
 *
 * @component
 */
export function IndirectCostSubcategorySelect({
  value,
  onChange,
  subcategories,
  isLoading = false,
  isError = false,
  errorMessage,
  placeholder,
  emptyLabel,
  loadingLabel,
  helperText,
  disabled = false,
  onOpenChange,
  onCreateSubcategory,
  createLabel = 'Add subcategory',
  createPlaceholder = 'New subcategory name...',
  createButtonLabel = 'Create',
  defaultOpen = false,
  trigger,
  className,
}: IndirectCostSubcategorySelectProps): ReactElement {
  const [open, setOpen] = useState(defaultOpen)
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleOpenChange = (newOpen: boolean): void => {
    setOpen(newOpen)
    onOpenChange?.(newOpen)
    if (!newOpen) {
      // Reset create state when closing
      setIsCreating(false)
      setNewName('')
    }
  }

  const handleStartCreating = (): void => {
    setIsCreating(true)
    // Focus input after render
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleCreate = async (): Promise<void> => {
    const trimmed = newName.trim()
    if (!trimmed || !onCreateSubcategory) return

    setIsSubmitting(true)
    try {
      const newUuid = await onCreateSubcategory(trimmed)
      onChange(newUuid)
      handleOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCreateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void handleCreate()
    } else if (e.key === 'Escape') {
      setIsCreating(false)
      setNewName('')
    }
  }

  const grouped = useMemo(() => {
    return subcategories.reduce<Record<'OPEX' | 'DA', IndirectCostSubcategoryDTO[]>>(
      (acc, item) => {
        if (item.system) return acc
        acc[item.category].push(item)
        return acc
      },
      { OPEX: [], DA: [] }
    )
  }, [subcategories])

  const selected = useMemo(
    () => subcategories.find(subcategory => subcategory.uuid === value) ?? null,
    [subcategories, value]
  )

  const renderSummary = (): ReactElement => {
    if (isError) {
      return (
        <span className="text-destructive text-sm">
          {errorMessage ?? 'Failed to load subcategories'}
        </span>
      )
    }

    if (!selected) {
      return <span className="text-muted-foreground text-sm">{placeholder}</span>
    }

    return (
      <span className="flex items-center gap-2 text-sm">
        <span>{selected.displayName}</span>
      </span>
    )
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          {trigger ?? (
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled || isLoading || isError}
              className="justify-between"
            >
              {renderSummary()}
              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" aria-hidden />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder={placeholder} disabled={isLoading} />
            <CommandList className="scrollbar-elegant">
              <CommandEmpty>{emptyLabel}</CommandEmpty>
              {isLoading ? (
                <div className="py-4 text-center text-sm text-muted-foreground">{loadingLabel}</div>
              ) : (
                <>
                  {(['OPEX', 'DA'] as const).map(category => {
                    const items = grouped[category]
                    if (items.length === 0) return null
                    return (
                      <CommandGroup key={category}>
                        {items.map(item => (
                          <CommandItem
                            key={item.uuid}
                            value={`${item.displayName} ${item.slug}`.toLowerCase()}
                            onSelect={() => {
                              onChange(item.uuid)
                              handleOpenChange(false)
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 size-4',
                                item.uuid === value ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <span className="flex flex-1 items-center justify-between gap-2">
                              <span>{item.displayName}</span>
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )
                  })}
                  {onCreateSubcategory && (
                    <CommandGroup>
                      {isCreating ? (
                        <div className="flex items-center gap-2 p-2">
                          <Input
                            ref={inputRef}
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={handleCreateKeyDown}
                            placeholder={createPlaceholder}
                            disabled={isSubmitting}
                            className="h-8 flex-1"
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => void handleCreate()}
                            disabled={!newName.trim() || isSubmitting}
                            className="h-8 px-3"
                          >
                            {isSubmitting ? <Spinner className="size-4" /> : createButtonLabel}
                          </Button>
                        </div>
                      ) : (
                        <CommandItem
                          value="__create_new__"
                          onSelect={handleStartCreating}
                          className="text-muted-foreground"
                        >
                          <Plus className="mr-2 size-4" />
                          {createLabel}
                        </CommandItem>
                      )}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {helperText ? <p className="text-xs text-muted-foreground">{helperText}</p> : null}
      {isError && errorMessage ? <p className="text-xs text-destructive">{errorMessage}</p> : null}
    </div>
  )
}

export default IndirectCostSubcategorySelect
